import { ReactComponent as MapIcon } from '@assets/icons/map-marker-alt-solid.svg';
import { inputDateFormat } from 'constant';
import { useShowSvcPrice } from 'context/ShowSvcPriceContext';

import { Flex, useTheme, VStack } from '@chakra-ui/react';

import {
  convertTimeToAMPMFormat,
  formatCurrency,
  getHumanReadableDate
} from 'utils';

import {
  AppointmentCheckoutData,
  AppointmentInterface,
  DiscountCode,
  LocationInterface
} from 'types';

import { Text } from 'ui';

interface AppointmentDetailsProps {
  data: AppointmentCheckoutData;
  promoData: DiscountCode | null;
  selectedAppointment: AppointmentInterface;
  selectedLocation: LocationInterface;
  productSum?: number;
}

const AppointmentDetails = ({
  selectedAppointment,
  selectedLocation,
  data,
  promoData,
  productSum = 0
}: AppointmentDetailsProps) => {
  const showSvcPrice = useShowSvcPrice();
  const {
    colors: {
      palette: {
        booking: {
          stepSuccess: { headerDetails }
        }
      }
    }
  } = useTheme();

  const totalPrice = data?.amount ?? 0;

  const discountPercentValue = promoData?.discount_prc || 0;
  const absoluteDiscountValue = totalPrice
    ? (totalPrice / 100) * discountPercentValue
    : 0;
  const appointmentPrice = absoluteDiscountValue
    ? totalPrice - absoluteDiscountValue
    : totalPrice;

  return (
    <VStack gap={0} borderRadius='6px' overflow='hidden'>
      <Flex
        w='full'
        justifyContent='center'
        bg={headerDetails.headerBg}
        py={{ base: 2, lg: 3 }}
      >
        <Text
          fontSize={{ base: '21px', md: '23px', lg: '25px' }}
          color={headerDetails.headerText}
          lineHeight='130%'
        >
          {getHumanReadableDate(
            selectedAppointment?.start_date ?? '',
            inputDateFormat
          ).toUpperCase()}
          {' at '}
          {convertTimeToAMPMFormat(selectedAppointment?.start_time ?? '')}
        </Text>
      </Flex>
      <Flex
        w='full'
        alignItems='center'
        bg={headerDetails.bodyBg}
        px={{ base: 15, md: '30px' }}
        py={{ base: '15px', md: '23px' }}
        gap={{ base: '10px', md: '40px' }}
        justifyContent='space-between'
      >
        <VStack gap={2} justifyContent='flex-start'>
          <Flex
            w='full'
            gap='14px'
            justifyContent='flex-start'
            alignItems='center'
          >
            <Text
              fontSize={{ base: '18px', md: '19px', lg: '21px' }}
              fontWeight='600'
              lineHeight='130%'
              textAlign='left'
              color={headerDetails.placeName}
            >
              {data?.store_name}
            </Text>
            <MapIcon width={14} height={18} color={headerDetails.iconColor} />
          </Flex>
          <Flex
            flexDirection={{ base: 'column', sm: 'row' }}
            columnGap={1}
            alignItems={{ base: 'flex-start', sm: 'center' }}
            w='full'
          >
            {selectedLocation?.addr1 && (
              <Text
                fontSize={{ base: '15px', md: '16px', lg: '17px' }}
                lineHeight='130%'
                color={headerDetails.address}
              >
                {selectedLocation?.addr1},
              </Text>
            )}
            <Text
              fontSize={{ base: '15px', md: '16px', lg: '17px' }}
              lineHeight='130%'
              color={headerDetails.address}
              textAlign='center'
            >
              {selectedLocation?.city
                ? `${selectedLocation?.city || selectedLocation?.full_name}, `
                : ''}
              {selectedLocation?.province
                ? `${selectedLocation?.province} `
                : ''}
              {selectedLocation?.postal ? selectedLocation?.postal : ''}
            </Text>
          </Flex>
        </VStack>
        <VStack gap='1px' alignItems='end' flexShrink={0}>
          {showSvcPrice && (
            <Text
              fontSize={{ base: '25px', md: '28px', lg: '32px' }}
              lineHeight='130%'
              textAlign='right'
              color={headerDetails.price}
            >
              {formatCurrency(
                appointmentPrice < 0
                  ? 0 + productSum
                  : appointmentPrice + productSum
              )}
            </Text>
          )}

          <Text
            fontSize={{ base: '12px', md: '14px', lg: '16px' }}
            lineHeight='140%'
            color={headerDetails.bookingText}
          >
            Booking # {data?.appt_id}
          </Text>
        </VStack>
      </Flex>
    </VStack>
  );
};

export default AppointmentDetails;
